import styles from './SimpleDataTable.module.css';

export type SimpleDataTableCellVariant = 'text' | 'coverage' | 'status';
export type SimpleDataTableCellEmphasis = 'strong' | 'default' | 'muted';
export type NutrientCoverageStatus = 'below' | 'above' | 'complete' | 'met';

export interface SimpleDataTableCoverageValue {
	percentage: number;
	status: NutrientCoverageStatus;
}

export interface SimpleDataTableColumn {
	key: string;
	label: string;
	variant?: SimpleDataTableCellVariant;
	emphasis?: SimpleDataTableCellEmphasis;
}

export interface SimpleDataTableRow {
	id: string;
	cells: Record<string, string>;
	coverage?: Record<string, SimpleDataTableCoverageValue>;
	isTotal?: boolean;
}

export interface SimpleDataTableProps {
	columns: SimpleDataTableColumn[];
	rows: SimpleDataTableRow[];
	minWidth?: string;
}

const STATUS_LABEL: Record<NutrientCoverageStatus, string> = {
	below: 'abaixo',
	above: 'acima',
	complete: 'completo',
	met: 'atendido',
};

const STATUS_TEXT_CLASS: Record<NutrientCoverageStatus, string> = {
	below: styles.statusOutlier,
	above: styles.statusOutlier,
	complete: styles.statusComplete,
	met: styles.statusMet,
};

const CoverageCell = ({ value }: { value?: SimpleDataTableCoverageValue }) => {
	if (!value) return <>—</>;

	const fillClass = value.percentage > 100 ? styles.coverageFillOver : styles.coverageFillOk;
	const fillWidth = Math.max(0, Math.min(value.percentage, 100));

	return (
		<div className={styles.coverageCell}>
			<div className={styles.coverageTrack}>
				<div className={`${styles.coverageFill} ${fillClass}`} style={{ width: `${fillWidth}%` }} />
			</div>
			<span className={`${styles.coveragePercentage} ${STATUS_TEXT_CLASS[value.status]}`}>
				{Math.round(value.percentage)}%
			</span>
		</div>
	);
};

const StatusCell = ({ value }: { value?: SimpleDataTableCoverageValue }) => {
	if (!value) return <>—</>;

	return (
		<span className={`${styles.statusLabel} ${STATUS_TEXT_CLASS[value.status]}`}>
			{STATUS_LABEL[value.status]}
		</span>
	);
};

const emphasisClass = (emphasis?: SimpleDataTableCellEmphasis) => {
	if (emphasis === 'strong') return styles.emphasisStrong;
	if (emphasis === 'muted') return styles.emphasisMuted;
	return '';
};

export const SimpleDataTable = ({ columns, rows, minWidth }: SimpleDataTableProps) => (
	<div className={styles.wrapper}>
		<table className={styles.table} style={minWidth ? { minWidth } : undefined}>
			<thead>
				<tr>
					{columns.map((column) => (
						<th key={column.key} className={styles.headerCell}>
							{column.label}
						</th>
					))}
				</tr>
			</thead>
			<tbody>
				{rows.map((row) => (
					<tr key={row.id} className={row.isTotal ? styles.totalRow : styles.dataRow}>
						{columns.map((column) => {
							const variant = column.variant ?? 'text';
							const className = row.isTotal
								? styles.totalCell
								: [
										styles.bodyCell,
										emphasisClass(column.emphasis),
										variant === 'coverage' ? styles.coverageBodyCell : '',
									]
										.filter(Boolean)
										.join(' ');

							return (
								<td key={`${row.id}-${column.key}`} className={className}>
									{variant === 'coverage' ? (
										<CoverageCell value={row.coverage?.[column.key]} />
									) : variant === 'status' ? (
										<StatusCell value={row.coverage?.[column.key]} />
									) : (
										(row.cells[column.key] ?? '—')
									)}
								</td>
							);
						})}
					</tr>
				))}
			</tbody>
		</table>
	</div>
);
